import styled from 'styled-components';
import { getSizeBy, getColorShade, getColor } from '../../../src/styled/utils';
import { groupRadius, groupBorder } from '../../styled/utils';
import { ButtonTextStyles } from '../typography';
import { Spinner } from '../spinner';
const BackgroundToTextColor = {
    primary: ['onPrimary', 'highEmphasis'],
    secondary: ['onSecondary', 'highEmphasis'],
    tertiary: ['onSurface', 'mediumEmphasis'],
    quaternary: ['onPrimary', 'highEmphasis'],
    background: ['onBackground', 'highEmphasis'],
    surface: ['onSurface', 'highEmphasis'],
    danger: ['onDanger', 'highEmphasis'],
    warning: ['onPrimary', 'highEmphasis'],
    success: ['onPrimary', 'highEmphasis']
};
const ROUND_BORDER_RADIUS = '50vh';
const getPaddingValue = (size) => {
    switch (size) {
        case 'small':
            return 1.5;
        case 'medium':
            return 2;
        case 'large':
            return 3;
        default:
            return 2;
    }
};
function getPadding(size) {
    return (theme) => {
        if (size === 'small') {
            return `${getSizeBy(1.5)(theme)} ${getSizeBy(2)(theme)}`;
        }
        if (size === 'large') {
            return `${getSizeBy(3)(theme)} ${getSizeBy(4)(theme)}`;
        }
        return `${getSizeBy(2)(theme)} ${getSizeBy(3)(theme)}`;
    };
}
function variantBg(accent) {
    return ({ theme, variant }) => {
        if (variant === 'filled') {
            return getColorShade('onSurface', accent)({ theme });
        }
        if (variant === 'outline') {
            return getColorShade('primary', accent)({ theme });
        }
        return '';
    };
}
export const StyledButtonBase = styled.button `
  position: relative;
  display: inline-grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: ${getColorShade('onSurface', 'mediumEmphasis')};
  column-gap: ${getSizeBy(2)};
  background-color: ${({ variant, $color }) => {
    /* variant="outline" buttons without $color prop provided */
    if (variant === 'outline' && $color === 'primary')
        return getColor('surface');
    if ((variant === 'filled' || variant === 'outline') && $color)
        return getColor($color);
    if (variant === 'transparent' || variant === 'text')
        return 'transparent';
    return getColor('surface');
}};
  border-color: ${({ variant }) => variant === 'outline' ? getColorShade('onSurface', 'outline') : 'transparent'};
  border-width: ${({ groupPosition }) => groupBorder(groupPosition)};
  border-radius: ${({ groupPosition, isRound }) => isRound ? ROUND_BORDER_RADIUS : groupRadius(groupPosition)};
  outline: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:disabled {
    color: ${getColorShade('onSurface', 'lowEmphasis')};
    background-color: ${({ variant }) => {
    if (variant === 'filled')
        return getColorShade('onSurface', 'hover');
    if (variant === 'text' || variant === 'transparent')
        return 'transparent';
    return getColor('surface');
}};
  }

  &:not(:disabled):hover {
    color: ${({ variant }) => {
    if (variant === 'text' || variant === 'transparent')
        return getColorShade('onSurface', 'highEmphasis');
}};
  }

  &:not(:disabled):active {
    color: ${({ variant }) => {
    if (variant === 'text' || variant === 'transparent')
        return getColor('primary');
}};
  }

  &:not(:disabled) svg path {
    fill: ${({ variant }) => {
    if (variant === 'filled')
        return getColorShade('onPrimary', 'mediumEmphasis');
    if (variant === 'outline')
        return getColorShade('onSurface', 'lowEmphasis');
    return 'currentcolor';
}};
  }

  &:disabled svg path {
    fill: ${getColorShade('onSurface', 'outline')};
  }
`;
export const StyledButton = styled(StyledButtonBase) `
  padding: ${({ size }) => getPadding(size)};
  color: ${({ variant, $color }) => {
    const buttonBgColor = $color;
    /**
     * Button with transparent background
     * or outlined button with no provided background $color prop (default is 'primary')
     */
    if (variant === 'text' ||
        variant === 'transparent' ||
        (variant === 'outline' && $color === 'primary'))
        return getColorShade('onSurface', 'highEmphasis');
    if ((variant === 'filled' || variant === 'outline') &&
        $color &&
        BackgroundToTextColor[buttonBgColor])
        return getColorShade(BackgroundToTextColor[buttonBgColor][0], BackgroundToTextColor[buttonBgColor][1]);
    return getColorShade('onSurface', 'highEmphasis');
}};
`;
export const IconStyledButton = styled(StyledButtonBase) `
  padding: ${({ theme, size = 'medium' }) => {
    const multiplier = getPaddingValue(size);
    return getSizeBy(multiplier)(theme);
}};
`;
export const ButtonOverlay = styled.span `
  position: absolute;
  top: -1px;
  left: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  border-radius: ${({ groupPosition, isRound }) => isRound ? ROUND_BORDER_RADIUS : groupRadius(groupPosition)};

  ${StyledButtonBase}:not(:disabled):hover > && {
    background-color: ${variantBg('hover')};
  }

  ${StyledButtonBase}:not(:disabled):active > && {
    background-color: ${variantBg('pressed')};
  }

  ${StyledButtonBase}:not(:disabled):focus > && {
    background-color: ${variantBg('focused')};
  }

  ${({ isLoading, variant, theme }) => isLoading &&
    `
      & {
        background-color: ${variantBg('focused')({ theme, variant })}
      }
    `}
`;
export const ButtonText = styled.span `
  ${ButtonTextStyles}
  position: relative;
  display: block;
  color: currentcolor;
  text-transform: capitalize;
  visibility: ${({ isLoading }) => (isLoading ? 'hidden' : 'initial')};
`;
export const SpinnerWrapper = styled.span `
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const StyledSpinner = styled(Spinner) `
  color: ${({ variant }) => getColorShade(variant === 'filled' ? 'onPrimary' : 'onSurface', 'mediumEmphasis')};
`;
